import { Checkbox, Skeleton } from "@fullscript/aviary-web";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Typography } from "@aviary";
import type { Brand as BrandFilter } from "@shared/components/CatalogSearch/CatalogSearch";
import { useBrandsQuery } from "@shared/data/queries/BrandsQuery/Brands.query";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import { l } from "@shared/locales/i18n";
import { StorePlatform } from "@shared/types/StorePlatform";
import type * as Types from "@shared/types/graphqlGenerated";

import * as styles from "./PopularBrands.styles";

type Brand = Pick<Types.BrandContextualFilters, "id" | "esResultCount"> & {
  brand: Pick<Types.Brand, "id" | "name" | "logo">;
};
type NonContextualBrand = Pick<Types.Brand, "id" | "name" | "logo">;

interface Props {
  isBrandActive: (id: String) => boolean;
  onBrandSelect: (brand: BrandFilter) => void;
  isContextual?: boolean;
  contextualData?: Brand[];
}

const PopularBrands = ({
  isBrandActive,
  onBrandSelect,
  isContextual = false,
  contextualData,
}: Props) => {
  const { t } = useTranslation();
  const { storePlatform } = useSharedGlobalConfig();

  const { loading, data } = useBrandsQuery({
    variables: {
      filters: { active: true, list: "TOP_BRANDS" },
      first: storePlatform !== StorePlatform.EMERSON ? 5 : 24,
    },
    skip: isContextual,
  });

  // new contextual filters that include result count
  const getPopularBrandsData = () => {
    const sortedContextual = [...(contextualData ?? [])];
    (sortedContextual ?? []).sort(
      (a: Brand, b: Brand) => (b?.esResultCount ?? 0) - (a?.esResultCount ?? 0)
    );
    const topFiveSortedContextual = sortedContextual.slice(0, 5);

    return topFiveSortedContextual;
  };

  const popularBrandsData: Brand[] = getPopularBrandsData();

  const renderCheckboxLabel = (brandWithCount: Brand) => {
    return `${brandWithCount.brand.name} (${brandWithCount.esResultCount})`;
  };

  const renderBrandCheckboxes = (): ReactNode[] => {
    return popularBrandsData.map((brandWithCount: Brand) => (
      <div css={styles.checkboxContainer} key={brandWithCount.brand.id}>
        <Checkbox
          id={brandWithCount.brand.id}
          checked={isBrandActive(brandWithCount.brand.id)}
          onChange={() =>
            onBrandSelect({
              id: brandWithCount.brand.id,
              name: brandWithCount.brand.name,
              logo: brandWithCount.brand?.logo ?? "",
            })
          }
          isFullWidth
          data-e2e={`${brandWithCount.brand.name} filter`}
        >
          {renderCheckboxLabel(brandWithCount)}
        </Checkbox>
      </div>
    ));
  };

  // old filters that don't include result count
  const nonContextualPopularBrandsData: NonContextualBrand[] = data?.viewer?.brands?.nodes ?? [];

  const renderNonContextualCheckboxLabel = (brand: NonContextualBrand) => {
    return brand.name;
  };

  const renderNonContextualBrandCheckboxes = (): ReactNode[] => {
    return nonContextualPopularBrandsData.map((brand: NonContextualBrand) => (
      <div css={styles.checkboxContainer} key={brand.id}>
        <Checkbox
          id={brand.id}
          checked={isBrandActive(brand.id)}
          onChange={() =>
            onBrandSelect({ id: brand.id, name: brand.name, logo: brand?.logo ?? "" })
          }
          isFullWidth
          data-e2e={`${brand.name} filter`}
        >
          {renderNonContextualCheckboxLabel(brand)}
        </Checkbox>
      </div>
    ));
  };

  const renderPopularBrandsCheckboxSkeleton = Array.from({ length: 11 }).map((_, index) => (
    <Skeleton key={index} type="typography" isFullWidth height={1.5} />
  ));

  if (!isContextual && loading) {
    return <div>{renderPopularBrandsCheckboxSkeleton}</div>;
  }

  // shared logic for both contextual and non-contextual filters
  return (
    <div>
      <Typography type="h5" css={styles.popularBrands}>
        {isContextual
          ? t(l.sharedCatalog.filter.SuggestedBrands)
          : t(l.sharedCatalog.filter.PopularBrands)}
      </Typography>
      {isContextual ? renderBrandCheckboxes() : renderNonContextualBrandCheckboxes()}
    </div>
  );
};

export { PopularBrands };
